import Vue from 'vue';

const BREAKPOINTS = {
    xs: 0, // Above -> Phones vertically
    sm: 640, // Above -> Phones horizontally
    md: 768, // Above -> Tablets vertically or large phones horizontally
    lg: 1024, // Above -> Tablets horizontally or large Tablets vertically, e.g., iPad Pro
    xl: 1280 // Above -> Laptops and Desktops
};

export function getBreakPointByWidth(width) {
    if (width >= BREAKPOINTS.xl) {
        return 'xl';
    }
    if (width >= BREAKPOINTS.lg) {
        return 'lg';
    }
    if (width >= BREAKPOINTS.md) {
        return 'md';
    }
    if (width >= BREAKPOINTS.sm) {
        return 'sm';
    }
    return 'xs';
}

export function getDefaultWidthByBreakpoint(breakpoint) {
    switch (breakpoint) {
        case 'xl':
            return 1920;
        case 'lg':
            return BREAKPOINTS.xl;
        case 'md':
            return BREAKPOINTS.lg;
        case 'sm':
            return BREAKPOINTS.md;
    }
    return 480;
}

class BreakpointService {

    constructor(defaultScreenWidth = 0, defaultScreenHeight = 0) {
        this.defaultWidth = defaultScreenWidth;
        this.defaultHeight = defaultScreenHeight;
        this.init()
    }

    init() {
        this.smAndDown = false;
        this.mdAndDown = false;

        this.xs = BREAKPOINTS.xs;
        this.sm = BREAKPOINTS.sm;
        this.md = BREAKPOINTS.md;
        this.lg = BREAKPOINTS.lg;
        this.xl = BREAKPOINTS.xl;
        this.update()

        if (typeof window === 'undefined') {
            return;
        }

        // Refresh the vars on resize
        window.addEventListener('resize', this.onResize.bind(this), {passive: true});
    }

    onResize() {
        clearTimeout(this.resizeTimeout);

        // Added debounce to match what
        // v-resize used to do but was
        // removed due to a memory leak
        // https://github.com/vuetifyjs/vuetify/pull/2997
        this.resizeTimeout = window.setTimeout(this.update.bind(this), 200)
    }

    update() {
        const height = this.getClientHeight();
        const width = this.getClientWidth();

        const scrollBarWidth = 10;

        this.xsOnly = width < this.sm;
        this.smOnly = width < this.md && !this.xsOnly;
        this.mdOnly = width < this.lg && !(this.smOnly || this.xsOnly);
        this.lgOnly = width < this.xl && !(this.mdOnly || this.smOnly || this.xsOnly);
        this.xlOnly = width >= (this.xl - scrollBarWidth);

        this.smAndDown = (this.xsOnly || this.smOnly) && !(this.mdOnly || this.lgOnly || this.xlOnly);
        this.smAndUp = !this.xsOnly && (this.smOnly || this.mdOnly || this.lgOnly || this.xlOnly);
        this.mdAndDown = (this.xsOnly || this.smOnly || this.mdOnly) && !(this.lgOnly || this.xlOnly);
        this.mdAndUp = !(this.xsOnly || this.smOnly) && (this.mdOnly || this.lgOnly || this.xlOnly);
        this.lgAndDown = (this.xsOnly || this.smOnly || this.mdOnly || this.lgOnly) && !this.xlOnly;
        this.lgAndUp = !(this.xsOnly || this.smOnly || this.mdOnly) && (this.lgOnly || this.xlOnly);

        // Set some helpers for global access that work on ssr and csr
        this.currentWidth = width;
        this.currentHeight = height;

        if (this.xlOnly) {
            this.current = 'xl';
        } else if (this.lgOnly) {
            this.current = 'lg';
        } else if (this.mdOnly) {
            this.current = 'md';
        } else if (this.smOnly) {
            this.current = 'sm';
        } else {
            this.current = 'xs';
        }
    }

    isSameOrDown(breakpoint) {
        switch (breakpoint) {
            case 'xs':
                return this.xsOnly;
            case 'sm':
                return this.smAndDown;
            case 'md':
                return this.mdAndDown;
            case 'lg':
                return this.lgAndDown;
        }
        return true;
    }

    isSameOrUp(breakpoint) {
        switch (breakpoint) {
            case 'sm':
                return this.smAndUp;
            case 'md':
                return this.mdAndUp;
            case 'lg':
                return this.lgAndUp;
            case 'xl':
                return this.xlOnly;
        }
        return true;
    }

    // Cross-browser support as described in:
    // https://stackoverflow.com/questions/1248081
    getClientWidth() {
        // Support SSR
        if (typeof document === 'undefined') {
            return this.defaultWidth
        }

        return Math.max(document.documentElement.clientWidth, window.innerWidth || this.defaultWidth)
    }

    getClientHeight() {
        // Support SSR
        if (typeof document === 'undefined') {
            return this.defaultHeight
        }
        return Math.max(document.documentElement.clientHeight, window.innerHeight || this.defaultHeight)
    }
}

export default (ctx, inject) => {

    // We guess the default size by the guessed device type (according to the user agent)
    let defaultScreenWidth = 375;
    let defaultScreenHeight = 375 / 0.5625; // 16:9

    if (ctx.$device.isTablet) {
        defaultScreenWidth = 768;
        defaultScreenHeight = 768 / 0.625; // 16:10
    } else if (ctx.$device.isDesktop) {
        defaultScreenWidth = 1280;
        defaultScreenHeight = 1280 * 0.5625; // 16:9
    }

    // We set default width and height based on the user's device since we can not extract the real screen size on server side
    inject('breakpoint', Vue.observable(new BreakpointService(defaultScreenWidth, defaultScreenHeight)))
}